import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import numeral from 'numeral';

import { useScreenQuery, Button, TextInput } from 'playup-web-core';

import { WithdrawMethods } from './USWithdrawService';
import { BodyContainer, BoldText, NormalText } from './USWithdraw';
import USAddAmountButtonWithMax from './USAddAmountButtonWithMax';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { createPaysafeWithdrawRequest } from '../../../api/users';

const USWithdrawPaymentCreation = ({ selectedWithdrawMethod, setWithdrawAmount }: Props) => {
	const { user } = React.useContext(PortalSessionContext);
	const { isSmall } = useScreenQuery();
	const history = useHistory();

	const [withdrawAmountError, setWithdrawAmountError] = React.useState<string>();
	const [selectedBankAccountForVipPreferred, setSelectedBankAccountForVipPreferred] =
		React.useState<UserPaysafeBankAccount>();

	const { register, watch, setValue, control } = useForm<FormData>({
		defaultValues: {
			inputWithdrawAmount: 0,
		},
	});
	const inputWithdrawAmount = watch('inputWithdrawAmount'); // in dollar

	const isVipPreferred = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.VipPreferred.id;
	const isSkrill = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.Skrill.id;
	const isSightline = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.Sightline.id;

	const selectedInternalWithdrawMethod = Object.values(WithdrawMethods).find((m) => {
		return m.id === selectedWithdrawMethod?.withdrawal_request_type.id;
	});

	const onAddAmount = (addedAmount: number | string) => {
		const withdrawableAmountInCents = user?.available_balance || 0;
		const withdrawableAmountInDollar = numeral(withdrawableAmountInCents).divide(100).value();

		if (withdrawableAmountInCents === 0) {
			setWithdrawAmountError("You don't have any balance to withdraw.");
			return;
		}

		if (typeof addedAmount === 'string') {
			if (addedAmount === 'Max') {
				setWithdrawAmount(withdrawableAmountInCents);
				setValue('inputWithdrawAmount', withdrawableAmountInDollar);
			}
			return;
		} else {
			const numeralInputWithdrawAmount = numeral(inputWithdrawAmount); // in dollar

			let subtotal = numeralInputWithdrawAmount.add(addedAmount).value();
			subtotal = subtotal > withdrawableAmountInDollar ? withdrawableAmountInDollar : subtotal;

			setWithdrawAmount(numeral(subtotal).multiply(100).value());
			setValue('inputWithdrawAmount', subtotal);
		}
	};

	const onBankAccountSelect = (bankAccountId: number) => {
		const newlySelectedBankAccount = selectedWithdrawMethod?.user_paysafe_bank_accounts?.find(
			(ba) => ba.id === bankAccountId
		);
		setSelectedBankAccountForVipPreferred(newlySelectedBankAccount);
	};

	const onWithdrawClick = async () => {
		if (!selectedWithdrawMethod || !selectedInternalWithdrawMethod) {
			return;
		}

		const withdrawRequestTypeId = selectedWithdrawMethod.withdrawal_request_type.id;

		let accountId = undefined as number | undefined;
		if (selectedWithdrawMethod.withdrawal_request_type.id === WithdrawMethods.VipPreferred.id) {
			accountId = selectedBankAccountForVipPreferred?.id;
		}
		if (
			selectedWithdrawMethod.withdrawal_request_type.id === WithdrawMethods.Skrill.id ||
			selectedWithdrawMethod.withdrawal_request_type.id === WithdrawMethods.Sightline.id
		) {
			accountId = selectedWithdrawMethod.user_paysafe_registration.id;
		}
		if (accountId === undefined) return;

		try {
			const response = await createPaysafeWithdrawRequest(
				withdrawRequestTypeId,
				numeral(inputWithdrawAmount).multiply(100).value(),
				accountId
			);

			history.push({
				pathname: `/success/${selectedInternalWithdrawMethod.name}`,
			});
		} catch (e: any) {
			if (e?.errors?.error) {
				setWithdrawAmountError(e?.errors.error);
			} else {
				setWithdrawAmountError('An unknown error occurred, please try again or contact our Customer Service.');
			}
		}
	};

	const onCancelClick = () => {
		history.push('/');
	};

	if (!selectedInternalWithdrawMethod && !selectedWithdrawMethod) {
		history.push('/');
	}

	//TODO: No Sightline information available. Need it from BE.
	return (
		<BodyContainer isSmallScreen={isSmall}>
			<LogoContainer>{selectedInternalWithdrawMethod?.icon}</LogoContainer>
			{isVipPreferred &&
				selectedWithdrawMethod?.user_paysafe_bank_accounts?.length &&
				selectedWithdrawMethod?.user_paysafe_bank_accounts?.length > 0 && (
					<SelectBankAccountSection>
						<NormalText isSmallScreen={isSmall}>Please select your desired destination account below:</NormalText>
						<RadioButtonGroup>
							{selectedWithdrawMethod?.user_paysafe_bank_accounts.map((bankAccount) => {
								return (
									<RadioButton key={bankAccount.id} onClick={() => onBankAccountSelect(bankAccount.id)}>
										<input type="radio" checked={selectedBankAccountForVipPreferred?.id === bankAccount.id} readOnly />
										{bankAccount.label}
									</RadioButton>
								);
							})}
						</RadioButtonGroup>
					</SelectBankAccountSection>
				)}
			{isSkrill && (
				<SkrillInfoSection>
					<NormalText isSmallScreen={isSmall}>
						{selectedWithdrawMethod?.user_paysafe_registration?.consumer_id}
					</NormalText>
					<GreyNormalText isSmallScreen={isSmall}>
						Please ensure that the email address on file with PlayUp matches that of your Skrill account.
					</GreyNormalText>
				</SkrillInfoSection>
			)}
			<SubtitleContainer>
				<BoldText isSmallScreen={isSmall}>Enter Your Withdrawal Amount ($)</BoldText>
			</SubtitleContainer>
			<Controller
				name="inputWithdrawAmount"
				control={control}
				rules={{ required: true }}
				render={({ field: { value, onChange } }) => {
					return (
						<TextInput
							kind="white"
							value={value}
							type="number"
							autoFocus
							{...register('inputWithdrawAmount', {
								valueAsNumber: true,
							})}
							error={withdrawAmountError}
							onChange={onChange}
						/>
					);
				}}
			/>
			<AddAmountButtonsContainer>
				<USAddAmountButtonWithMax additionalAmount={20} onClick={onAddAmount} />
				<USAddAmountButtonWithMax additionalAmount={50} onClick={onAddAmount} />
				<USAddAmountButtonWithMax additionalAmount={100} onClick={onAddAmount} />
				<USAddAmountButtonWithMax additionalAmount={250} onClick={onAddAmount} />
				<USAddAmountButtonWithMax additionalAmount={'Max'} onClick={onAddAmount} />
			</AddAmountButtonsContainer>
			<SubmitButton
				isSmallScreen={isSmall}
				kind="white"
				type="submit"
				onClick={onWithdrawClick}
				disabled={
					!selectedWithdrawMethod ||
					inputWithdrawAmount === 0 ||
					(isVipPreferred && !selectedBankAccountForVipPreferred)
				}
			>
				Continue
			</SubmitButton>
			<CancelButton isSmallScreen={isSmall} kind="hollow-black" type="submit" onClick={onCancelClick}>
				Cancel
			</CancelButton>
		</BodyContainer>
	);
};

export default USWithdrawPaymentCreation;

interface Props {
	selectedWithdrawMethod?: WithdrawalRequestType;
	setWithdrawAmount: (withdrawAmount: number) => void;
}

interface FormData {
	inputWithdrawAmount: number;
}

const LogoContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 100%;
`;

const SelectBankAccountSection = styled.section`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-top: 25px;
	margin-bottom: 25px;
	padding-bottom: 25px;

	border-style: solid;
	border-bottom-width: 1px;
	border-color: ${({ theme }) => theme.colors.lightGreyR};
`;

const RadioButtonGroup = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
`;

const RadioButton = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	cursor: pointer;
	margin-top: 20px;

	input {
		margin-right: 10px;
	}
`;

const SkrillInfoSection = styled.section`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-top: 25px;
	margin-bottom: 25px;
	padding-bottom: 25px;

	p {
		margin-bottom: 10px;
	}
`;

const GreyNormalText = styled.p<{ isSmallScreen: boolean }>`
	margin: 0;
	fontsize: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	color: ${({ theme }) => theme.colors.greyR};
`;

const SubtitleContainer = styled.div`
	text-align: center;
	margin-bottom: 15px;
`;

const AddAmountButtonsContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin-top: 15px;
	margin-bottom: 15px;
`;

const SubmitButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '30px' : '40px')};
	margin-bottom: 10px;
`;

const CancelButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;
